import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  CustomUserField,
  toCustomFieldKey
} from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import { compact } from 'lodash';
import { useState } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { DeleteButton } from '../../../../../../../components/DeletionConfirmation';
import { DialogActionsWithSlots } from '../../../../../../../components/DialogActionsWithSlots';
import { Form } from '../../../../../../../components/Form';
import { FormField } from '../../../../../../../components/Form/FormField';
import { FormSubmitButton } from '../../../../../../../components/Form/FormSubmitButton';
import { DateTimeFieldNullable } from '../../../../../../../components/Time/DateTimeField';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import {
  getUsersByOrgId,
  matchUserBySearchTerm,
  toFullName,
  toSearchTermRegexp,
  useUserById
} from '../../../../../../../services/user';

const UserRelationshipGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridColumnGap: p.theme.spacing(3),
  gridRowGap: p.theme.spacing(2)
}));

const UserAvatarNull = () => {
  return <div>None</div>;
};

const UserAvatarNonNull = ({ userId }: { userId: string }) => {
  const [user, loading] = useUserById(userId);
  const asString = loading
    ? `Loading... (${userId})`
    : user
    ? toFullName(user.data)
    : `User not found (${userId})`;

  return <div>{asString}</div>;
};

const UserAvatarNullable = ({ userId }: { userId: string | null }) => {
  return userId ? <UserAvatarNonNull userId={userId} /> : <UserAvatarNull />;
};

const UserRelationshipDialog = ({
  open,
  onClose,
  value,
  onChange,
  orgId
}: {
  open: boolean;
  onClose: () => void;
  value: string | null;
  onChange: (nextValue: string | null) => void;
  orgId: string;
}) => {
  // current
  // next
  // -> allow removal
  const [nextUser, setNextUser] = useState<Doc<IUser> | null>(null);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Update Relationship</DialogTitle>
      <DialogContent>
        <UserRelationshipGrid>
          <div>
            <strong>Current:</strong>
          </div>
          <UserAvatarNullable userId={value} />
          <div>
            <strong>Next:</strong>
          </div>
          <div>
            <AutocompleteSingle
              value={nextUser}
              onChange={setNextUser}
              getOptionLabel={(u) => toFullName(u.data)}
              options={() => getUsersByOrgId(orgId)}
              filterOptions={(us, s) => {
                const t = toSearchTermRegexp(s.inputValue);
                return us.filter((u) => matchUserBySearchTerm(t, u));
              }}
              fullWidth
            />
          </div>
        </UserRelationshipGrid>
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <Button
            color="secondary"
            variant="outlined"
            onClick={() => {
              onChange(null);
              onClose();
            }}
          >
            Remove
          </Button>
        }
        right={
          <>
            <Button color="primary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!nextUser}
              onClick={() => {
                if (nextUser) {
                  onChange(nextUser.id);
                  onClose();
                  setNextUser(null);
                }
              }}
            >
              Update
            </Button>
          </>
        }
      ></DialogActionsWithSlots>
    </Dialog>
  );
};

const UserRelationshipInput = ({
  label,
  value,
  valueAsString,
  onChange,
  fullWidth,
  autoFocus,
  orgId
}: {
  label: string;
  value: string | null; // a userId
  valueAsString: string;
  onChange: (nextValue: string | null) => void;
  fullWidth?: boolean;
  autoFocus?: boolean;
  orgId: string;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  return (
    <>
      <TextField
        label={label}
        autoFocus={autoFocus}
        fullWidth={fullWidth}
        value={valueAsString}
        onClick={openDialog}
        variant="outlined"
        InputProps={{ readOnly: true }}
      />
      <UserRelationshipDialog
        open={dialogOpen}
        value={value}
        onChange={onChange}
        onClose={closeDialog}
        orgId={orgId}
      />
    </>
  );
};

const UserRelationshipFieldNull = (props: {
  label: string;
  value: null;
  onChange: (nextValue: string | null) => void;
  fullWidth?: boolean;
  autoFocus?: boolean;
  orgId: string;
}) => {
  return <UserRelationshipInput {...props} valueAsString="None" />;
};

const UserRelationshipFieldNonNull = (props: {
  label: string;
  value: string;
  onChange: (nextValue: string | null) => void;
  fullWidth?: boolean;
  autoFocus?: boolean;
  orgId: string;
}) => {
  const [user, loading] = useUserById(props.value);
  const valueAsString = loading
    ? `Loading... (${props.value})`
    : user
    ? toFullName(user.data)
    : `User not found (${props.value})`;
  return <UserRelationshipInput {...props} valueAsString={valueAsString} />;
};

const UserRelationshipField = ({
  value,
  ...props
}: {
  label: string;
  value: string | null; // a userId
  onChange: (nextValue: string | null) => void;
  fullWidth?: boolean;
  autoFocus?: boolean;
  orgId: string;
}) => {
  return value ? (
    <UserRelationshipFieldNonNull value={value} {...props} />
  ) : (
    <UserRelationshipFieldNull value={null} {...props} />
  );
};

const FormGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gridColumnGap: p.theme.spacing(1),
  gridRowGap: p.theme.spacing(2),
  alignItems: 'center'
}));

export const UserDetailsDialog = ({
  open,
  onSubmit,
  onRemove,
  onClose,
  user,
  userId,
  customFields
}: {
  open: boolean;
  onSubmit: (nextUser: IUser) => Promise<any>;
  onRemove?: () => Promise<any>;
  onClose: () => void;
  user: IUser;
  userId?: string;
  customFields: CustomUserField[];
}) => {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {compact([toFullName(user), user.email, userId]).join(' - ')}
      </DialogTitle>
      <Form<IUser>
        initialValues={user}
        onSubmit={(nextUser) => onSubmit(nextUser).then(onClose)}
      >
        {({ submitting }) => (
          <>
            <DialogContent>
              <FormGrid>
                <FormField<IUser['email']> name="email" parse={(t) => t}>
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Email"
                    />
                  )}
                </FormField>

                <FormField<IUser['personalEmail']>
                  name="personalEmail"
                  parse={(t) => t}
                >
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Personal Email"
                    />
                  )}
                </FormField>
                <FormField<IUser['firstName']>
                  name="firstName"
                  parse={(t) => t}
                >
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="First Name"
                    />
                  )}
                </FormField>
                <FormField<IUser['middleName']>
                  name="middleName"
                  parse={(t) => t}
                >
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Middle Name"
                    />
                  )}
                </FormField>
                <FormField<IUser['preferredName']>
                  name="preferredName"
                  parse={(t) => t}
                >
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Preferred Name"
                    />
                  )}
                </FormField>
                <FormField<IUser['lastName']> name="lastName" parse={(t) => t}>
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Last Name"
                    />
                  )}
                </FormField>
                <FormField<IUser['location']> name="location" parse={(t) => t}>
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Location"
                    />
                  )}
                </FormField>
                <FormField<IUser['role']> name="role" parse={(t) => t}>
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Role"
                    />
                  )}
                </FormField>
                <FormField<IUser['department']>
                  name="department"
                  parse={(t) => t}
                >
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Department"
                    />
                  )}
                </FormField>
                <FormField<IUser['team']> name="team" parse={(t) => t}>
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Team"
                    />
                  )}
                </FormField>
                <FormField<IUser['managerId']> name="managerId">
                  {({ input }) => (
                    <UserRelationshipField
                      label="Manager"
                      value={input.value}
                      onChange={input.onChange}
                      fullWidth
                      orgId={user.orgId}
                    />
                  )}
                </FormField>
                <FormField<IUser['hireDate']> name="hireDate">
                  {({ input }) => (
                    <DateTimeFieldNullable
                      label="First day at work"
                      value={input.value}
                      onChange={input.onChange}
                      tz="CET"
                      fullWidth
                      minutes
                    />
                  )}
                </FormField>

                <FormField<IUser['terminationDate']> name="terminationDate">
                  {({ input }) => (
                    <DateTimeFieldNullable
                      label="Last day at work"
                      value={input.value}
                      onChange={input.onChange}
                      tz="CET"
                      fullWidth
                      minutes
                    />
                  )}
                </FormField>

                <FormField<IUser['dateOfBirth']> name="dateOfBirth">
                  {({ input }) => (
                    <DateTimeFieldNullable
                      label="Date of Birth"
                      value={input.value}
                      onChange={input.onChange}
                      tz="CET"
                      fullWidth
                      minutes
                    />
                  )}
                </FormField>

                <FormField<IUser['phoneNumber']> name="phoneNumber">
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Phone Number"
                    />
                  )}
                </FormField>
                <FormField<IUser['alternateId']> name="alternateId">
                  {({ input }) => (
                    <TextField
                      {...input}
                      fullWidth
                      variant="outlined"
                      label="Alternate ID"
                    />
                  )}
                </FormField>
              </FormGrid>

              {!!customFields.length && (
                <>
                  <h4>Custom Fields</h4>
                  <FormGrid>
                    {customFields.map((f) => {
                      const key = toCustomFieldKey(f.key);
                      return (
                        <FormField name={key} key={key}>
                          {({ input }) => {
                            if (f.type === 'relationship') {
                              return (
                                <UserRelationshipField
                                  label={f.label}
                                  value={input.value || null}
                                  onChange={input.onChange}
                                  fullWidth
                                  orgId={user.orgId}
                                />
                              );
                            }
                            if (f.type === 'string') {
                              return (
                                <TextField
                                  {...input}
                                  value={input.value || ''}
                                  fullWidth
                                  variant="outlined"
                                  label={f.label}
                                />
                              );
                            }
                            if (f.type === 'date') {
                              if (
                                input.value &&
                                typeof input.value === 'string'
                              ) {
                                return (
                                  <Typography variant="caption">
                                    {f.label} date type mismatch: expected
                                    timestamp, got date
                                  </Typography>
                                );
                              }
                              return (
                                <DateTimeFieldNullable
                                  label={f.label}
                                  value={input.value || null}
                                  onChange={input.onChange}
                                  tz="CET"
                                  fullWidth
                                  minutes
                                />
                              );
                            }

                            return (
                              <div>Field type not implemented: {f.type}</div>
                            );
                          }}
                        </FormField>
                      );
                    })}
                  </FormGrid>
                </>
              )}
            </DialogContent>
            <DialogActionsWithSlots
              left={
                onRemove && (
                  <DeleteButton
                    onDelete={() => onRemove().then(onClose)}
                    variant="contained"
                    color="secondary"
                  >
                    Remove user
                  </DeleteButton>
                )
              }
              right={
                <>
                  <Button onClick={onClose}>Cancel</Button>
                  <FormSubmitButton
                    submitting={submitting}
                    submitComponent={'Updating...'}
                    disabled={false}
                  >
                    Save
                  </FormSubmitButton>
                </>
              }
            />
          </>
        )}
      </Form>
    </Dialog>
  );
};
